import React from 'react';

/**
 * The animated MINDALITIX wordmark. Restored at the owner's request after a
 * redesign pass replaced it with a plain spinner — only the page background was
 * moved onto the theme token so it sits correctly in dark mode.
 */

const MainLoader: React.FC = () => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background-main p-4">
      <div className="flex items-center flex-wrap justify-center gap-1 max-w-full overflow-hidden">
        {/* Hidden SVG with gradient definitions */}
        <svg height="0" width="0" viewBox="0 0 64 64" className="absolute">
          <defs xmlns="http://www.w3.org/2000/svg">
            <linearGradient gradientUnits="userSpaceOnUse" y2="2" x2="0" y1="62" x1="0" id="gradientB">
              <stop stopColor="#973BED" />
              <stop stopColor="#007CFF" offset="1" />
            </linearGradient>
            <linearGradient gradientUnits="userSpaceOnUse" y2="0" x2="0" y1="64" x1="0" id="gradientC">
              <stop stopColor="#FFC800" />
              <stop stopColor="#F0F" offset="1" />
              <animateTransform 
                repeatCount="indefinite" 
                keySplines=".42,0,.58,1;.42,0,.58,1;.42,0,.58,1;.42,0,.58,1;.42,0,.58,1;.42,0,.58,1;.42,0,.58,1;.42,0,.58,1" 
                keyTimes="0; 0.125; 0.25; 0.375; 0.5; 0.625; 0.75; 0.875; 1" 
                dur="8s" 
                values="0 32 32;-270 32 32;-270 32 32;-540 32 32;-540 32 32;-810 32 32;-810 32 32;-1080 32 32;-1080 32 32" 
                type="rotate" 
                attributeName="gradientTransform"
              />
            </linearGradient>
            <linearGradient gradientUnits="userSpaceOnUse" y2="2" x2="0" y1="62" x1="0" id="gradientD">
              <stop stopColor="#00E0ED" />
              <stop stopColor="#00DA72" offset="1" />
            </linearGradient>
          </defs>
        </svg>

        {/* M */}
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 64 64" className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 inline-block flex-shrink-0">
          <path 
            strokeLinejoin="round" 
            strokeLinecap="round" 
            strokeWidth="8" 
            stroke="url(#gradientB)" 
            d="M 8,56 V 8 h 8 l 8,16 8,-16 h 8 v 48 h -8 V 24 l -6,12 h -4 l -6,-12 v 32 z" 
            className="animate-dash" 
            pathLength="360"
          />
        </svg>

        {/* I */}
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 64 64" className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 inline-block flex-shrink-0">
          <path 
            strokeLinejoin="round" 
            strokeLinecap="round" 
            strokeWidth="8" 
            stroke="url(#gradientC)" 
            d="M 24,8 h 16 v 8 h -4 v 40 h 4 v 8 H 24 v -8 h 4 V 16 h -4 z" 
            className="animate-dash" 
            pathLength="360"
          />
        </svg>

        {/* N */}
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 64 64" className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 inline-block flex-shrink-0">
          <path 
            strokeLinejoin="round" 
            strokeLinecap="round" 
            strokeWidth="8" 
            stroke="url(#gradientD)" 
            d="M 8,56 V 8 h 8 l 24,32 V 8 h 8 v 48 h -8 L 16,24 v 32 z" 
            className="animate-dash" 
            pathLength="360"
          />
        </svg>

        {/* D */}
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 64 64" className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 inline-block flex-shrink-0">
          <path 
            strokeLinejoin="round" 
            strokeLinecap="round" 
            strokeWidth="8" 
            stroke="url(#gradientB)" 
            d="M 8,8 h 20 c 12,0 20,8 20,24 0,16 -8,24 -20,24 H 8 Z m 8,8 v 32 h 12 c 8,0 12,-4 12,-16 0,-12 -4,-16 -12,-16 z" 
            className="animate-dash" 
            pathLength="360"
          />
        </svg>

        {/* A */}
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 64 64" className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 inline-block flex-shrink-0">
          <path 
            strokeLinejoin="round" 
            strokeLinecap="round" 
            strokeWidth="8" 
            stroke="url(#gradientC)" 
            d="M 32,8 L 8,56 h 8 l 4,-8 h 24 l 4,8 h 8 z M 20,40 l 8,-16 8,16 z" 
            className="animate-dash" 
            pathLength="360"
          />
        </svg>

        {/* L */}
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 64 64" className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 inline-block flex-shrink-0">
          <path 
            strokeLinejoin="round" 
            strokeLinecap="round" 
            strokeWidth="8" 
            stroke="url(#gradientD)" 
            d="M 8,8 v 48 h 40 v -8 H 16 V 8 z" 
            className="animate-dash" 
            pathLength="360"
          />
        </svg>

        {/* I */}
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 64 64" className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 inline-block flex-shrink-0">
          <path 
            strokeLinejoin="round" 
            strokeLinecap="round" 
            strokeWidth="8" 
            stroke="url(#gradientB)" 
            d="M 24,8 h 16 v 8 h -4 v 40 h 4 v 8 H 24 v -8 h 4 V 16 h -4 z" 
            className="animate-dash" 
            pathLength="360"
          />
        </svg>

        {/* T */}
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 64 64" className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 inline-block flex-shrink-0">
          <path 
            strokeLinejoin="round" 
            strokeLinecap="round" 
            strokeWidth="8" 
            stroke="url(#gradientC)" 
            d="M 8,8 h 48 v 8 H 36 v 40 h -8 V 16 H 8 z" 
            className="animate-dash" 
            pathLength="360"
          />
        </svg>

        {/* I */}
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 64 64" className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 inline-block flex-shrink-0">
          <path 
            strokeLinejoin="round" 
            strokeLinecap="round" 
            strokeWidth="8" 
            stroke="url(#gradientD)" 
            d="M 24,8 h 16 v 8 h -4 v 40 h 4 v 8 H 24 v -8 h 4 V 16 h -4 z" 
            className="animate-dash" 
            pathLength="360"
          />
        </svg>

        {/* X */}
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 64 64" className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 inline-block flex-shrink-0">
          <path 
            strokeLinejoin="round" 
            strokeLinecap="round" 
            strokeWidth="8" 
            stroke="url(#gradientB)" 
            d="M 8,8 L 32,32 L 56,8 h -12 L 32,20 L 20,8 Z M 20,56 L 32,44 L 44,56 h 12 L 32,32 L 8,56 Z" 
            className="animate-dash" 
            pathLength="360"
          />
        </svg>

        <style>{`
          @keyframes dashArray {
            0% {
              stroke-dasharray: 0 1 359 0;
            }
            50% {
              stroke-dasharray: 0 359 1 0;
            }
            100% {
              stroke-dasharray: 359 1 0 0;
            }
          }

          @keyframes dashOffset {
            0% {
              stroke-dashoffset: 365;
            }
            100% {
              stroke-dashoffset: 5;
            }
          }

          .animate-dash {
            animation: dashArray 2s ease-in-out infinite,
                       dashOffset 2s linear infinite;
          }
        `}</style>
      </div>
    </div>
  );
};

export default MainLoader;